import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SuccessDialog from '@/components/dialog/SuccessDialog';
import { Icons } from '@/icons';
import { useDispatch } from 'react-redux';
import { fetchGamicById, updateGamic } from '@/store/Gamic';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchCategories } from '@/store/Category';
import RemoteFile from '@/helper/RemoteFile';
import {
  injectGamicConsoleBridge,
  shouldIgnoreGamicPreviewLog,
} from '@/helper/gamicConsoleBridge';

const EditGamics: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [agree, setAgree] = useState(false);
  const [open, setOpen] = useState(false);
  const [gameFile, setGameFile] = useState<File | null>(null);
  const [oldGameUrl, setOldGameUrl] = useState('');
  const [gamicName, setGamicName] = useState('');
  const [downloadLink, setDownloadLink] = useState('');
  // WL-DL-V1
  const [iosDownloadUrl, setIosDownloadUrl] = useState('');
  const [androidDownloadUrl, setAndroidDownloadUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [oldThumbnailUrl, setOldThumbnailUrl] = useState('');
  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [instrumentedGameUrl, setInstrumentedGameUrl] = useState<string | null>(
    null,
  );
  const [logs, setLogs] = useState<string[]>([]);
  const [remoteLogWarned, setRemoteLogWarned] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const logsRef = useRef<HTMLPreElement | null>(null);
  const instrumentedUrlRef = useRef<string | null>(null);
  const queueRef = useRef<string[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();

  const appendLog = (message: string) => {
    setLogs((prev) => [...prev, message]);
  };

  useEffect(() => {
    const categoryData = async () => {
      const res = await dispatch(fetchCategories());
      extractCategories(res.payload.data);
      function extractCategories(data: any[]) {
        const categoryNames = data.map((item) => item.name);
        setCategories(categoryNames);
      }

      const resGamic = await dispatch(fetchGamicById(id as string));
      console.log(resGamic.payload.data);
      const g = resGamic.payload.data[0];
      setOldGameUrl(g.file_url);
      setGamicName(g.title);
      setDownloadLink(g.download_url);
      // WL-DL-V1: hydrate split fields; backend backfilled these from
      // download_url for existing rows.
      setIosDownloadUrl(g.ios_download_url || '');
      setAndroidDownloadUrl(g.android_download_url || '');
      setDescription(g.description);
      setTags(g.tags || []);
      setSelectedCategories(g.categories || []);
      setOldThumbnailUrl(g.image_url);
    };
    categoryData();
  }, []);

  useEffect(() => {
    if (gameFile) {
      const url = URL.createObjectURL(gameFile);
      setGameUrl(url);

      return () => {
        URL.revokeObjectURL(url); // Cleanup on unmount or when gameFile changes
      };
    } else {
      setGameUrl(null);
    }
  }, [gameFile]);

  useEffect(() => {
    if (!gameFile) {
      if (instrumentedUrlRef.current) {
        URL.revokeObjectURL(instrumentedUrlRef.current);
        instrumentedUrlRef.current = null;
      }
      setInstrumentedGameUrl(null);
      return;
    }

    setLogs([]);
    setRemoteLogWarned(false);
    appendLog('[INFO] Loading selected HTML file...');

    let active = true;
    gameFile
      .text()
      .then((html) => {
        if (!active) return;
        const instrumentedHtml = injectGamicConsoleBridge(html);
        const url = URL.createObjectURL(
          new Blob([instrumentedHtml], { type: 'text/html' }),
        );
        setInstrumentedGameUrl((previousUrl) => {
          if (previousUrl) URL.revokeObjectURL(previousUrl);
          instrumentedUrlRef.current = url;
          return url;
        });
        appendLog('[INFO] Preview started.');
      })
      .catch(() => {
        appendLog('[ERROR] Could not read selected HTML file.');
      });

    return () => {
      active = false;
    };
  }, [gameFile]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!event.data?.__gamicPreviewLog) return;
      const level = String(event.data.level || 'log').toUpperCase();
      const message = String(event.data.message || '');
      if (shouldIgnoreGamicPreviewLog(message)) return;
      queueRef.current.push(`[${level}] ${message}`);
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (queueRef.current.length === 0) return;
      const pending = [...queueRef.current];
      queueRef.current = [];
      setLogs((prev) => [...prev, ...pending].slice(-5000));
    }, 100);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (instrumentedUrlRef.current) {
        URL.revokeObjectURL(instrumentedUrlRef.current);
        instrumentedUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!logsRef.current) return;
    logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs]);

  const setIsMt = () => {
    setGameFile(null);
    setGamicName('');
    setDownloadLink('');
    setDescription('');
    setTags([]);
    setInputValue('');
    setSelectedCategories([]);
    setThumbnailFile(null);
    setAgree(false);
  };
  const handleSubmit = async () => {
    if (!agree) return;

    // WL-DL-V1: validate store hostnames.
    const ios = iosDownloadUrl.trim();
    const android = androidDownloadUrl.trim();
    if (ios && !ios.toLowerCase().includes('apps.apple.com')) {
      setLinkError('iOS link must be an apps.apple.com URL');
      return;
    }
    if (android && !android.toLowerCase().includes('play.google.com')) {
      setLinkError('Android link must be a play.google.com URL');
      return;
    }
    setLinkError(null);

    const formData = new FormData();
    if (gameFile) {
      formData.append('gamicsFile', gameFile);
    }
    formData.append('title', gamicName);
    formData.append('downloadUrl', downloadLink || ios || android);
    formData.append('iosDownloadUrl', ios);
    formData.append('androidDownloadUrl', android);
    formData.append('description', description);
    formData.append('tags', JSON.stringify(tags));
    formData.append('categorys', JSON.stringify(selectedCategories));
    if (thumbnailFile) {
      formData.append('gamicsPic', thumbnailFile);
    }

    try {
      toast.info('Submitting gamic...');
      if (!id) {
        throw new Error('Gamic ID is required for update');
      }
      const res = await dispatch(updateGamic({ id, formData })).unwrap();
      setOpen(true);
      setIsMt();
    } catch (err: any) {
      console.error(err);
      toast.error('Gamic creation failed');
    }
  };

  const handleClose = () => {
    setOpen(false);
    navigate('/publisher/gamics');
  };

  const onGameFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setGameFile(file);
    if (file) {
      toast.info(`Uploading: ${file.name}`);
    }
  };

  const onThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setThumbnailFile(file);
    if (file) {
      toast.info(`Uploading: ${file.name}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!tags.includes(inputValue.trim())) {
        setTags([...tags, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  const removeTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (selected && !selectedCategories.includes(selected)) {
      setSelectedCategories([...selectedCategories, selected]);
    }
    e.target.value = ''; // Reset dropdown after selection
  };

  const removeCategory = (index: number) => {
    setSelectedCategories(selectedCategories.filter((_, i) => i !== index));
  };

  const onPreviewLoad = () => {
    appendLog('[INFO] Preview frame loaded.');
    if (gameFile) appendLog('[INFO] Console capture attached.');

    if (!gameFile && !remoteLogWarned) {
      appendLog(
        '[WARN] Realtime console logs may be unavailable for remote file preview.',
      );
      setRemoteLogWarned(true);
    }
  };

  return (
    <>
      <ToastContainer position='top-right' autoClose={3000} />
      <div className='mx-auto gap-6 grid lg:grid-cols-2 lg:gap-8 w-full xl:gap-10 2xl:gap-12'>
        {/* ───────── Left column – Form ───────── */}
        <div className='flex flex-col justify-between bg-white rounded-[24px] p-6 lg:p-6 xl:p-8 2xl:p-10 w-full lg:w-[35vw] xl:w-[38vw] 2xl:w-[742px] min-w-0'>
          <div className='space-y-2'>
            {/* ───────── Game File Upload ───────── */}
            <div className='space-y-2 border-b border-[#B9B9B9] bg-[#F5F5F5] p-4 rounded-tl-[4px] rounded-tr-[4px]'>
              <p className='text-base font-semibold text-[#69666A]'>
                Game File
              </p>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <label className='cursor-pointer rounded-full border border-black px-4 py-2 text-sm font-medium bg-transparent'>
                    Change File
                    <input
                      type='file'
                      accept='.html'
                      className='hidden'
                      onChange={onGameFileChange}
                    />
                  </label>
                  <span className='text-xs text-gray-500'>HTML5 file only</span>
                </div>
                {(gameFile || oldGameUrl) && (
                  <span className='text-sm text-[#333]'>
                    Is Selected
                  </span>
                )}
              </div>
            </div>

            {/* ───────── Game Name ───────── */}
            <div className='border-b border-[#B9B9B9] bg-[#F5F5F5] px-4 py-2 rounded-tl-[4px] rounded-tr-[4px]'>
              <p className='mb-1 text-sm font-semibold text-[#69666A]'>
                Gamic Name
              </p>
              <input
                type='text'
                value={gamicName}
                onChange={(e) => setGamicName(e.target.value)}
                placeholder='Enter gamic name here'
                className='w-full bg-[#F5F5F5] text-base placeholder-[#c8c8c8] focus:outline-none'
              />
            </div>

            {/* ───────── Download Links (WL-DL-V1) ───────── */}
            <div className='border-b border-[#B9B9B9] bg-[#F5F5F5] px-4 py-2 rounded-tl-[4px] rounded-tr-[4px]'>
              <p className='mb-1 text-sm font-semibold text-[#69666A]'>
                App Store URL (iOS)
              </p>
              <input
                type='url'
                value={iosDownloadUrl}
                onChange={(e) => setIosDownloadUrl(e.target.value)}
                placeholder='https://apps.apple.com/app/...'
                className='w-full bg-[#F5F5F5] text-base placeholder-[#c8c8c8] focus:outline-none'
              />
            </div>
            <div className='border-b border-[#B9B9B9] bg-[#F5F5F5] px-4 py-2'>
              <p className='mb-1 text-sm font-semibold text-[#69666A]'>
                Google Play URL (Android)
              </p>
              <input
                type='url'
                value={androidDownloadUrl}
                onChange={(e) => setAndroidDownloadUrl(e.target.value)}
                placeholder='https://play.google.com/store/apps/details?id=...'
                className='w-full bg-[#F5F5F5] text-base placeholder-[#c8c8c8] focus:outline-none'
              />
            </div>
            {linkError && (
              <p className='text-xs text-red-600 mt-1 px-1'>{linkError}</p>
            )}

            {/* ───────── Description ───────── */}
            <div className='border-b border-[#B9B9B9] bg-[#F5F5F5] px-4 py-2 rounded-tl-[4px] rounded-tr-[4px]'>
              <p className='mb-1 text-sm font-semibold text-[#69666A]'>
                Descriptions
              </p>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='Write a short game description…'
                className='w-full resize-none bg-[#F5F5F5] text-base placeholder-[#c8c8c8] focus:outline-none'
              />
            </div>

            {/* ───────── Tags ───────── */}
            <div className='border-b border-[#B9B9B9] bg-[#F5F5F5] px-4 py-2 rounded-[4px]'>
              <p className='mb-2 text-sm font-semibold text-[#69666A]'>Tags</p>

              <div className='flex flex-wrap gap-2 mb-2'>
                {tags.map((tag, index) => (
                  <div
                    key={index}
                    className='flex items-center gap-2 px-3 py-1 bg-white border border-[#B9B9B9] rounded-full text-sm text-[#333]'
                  >
                    <span>{tag}</span>
                    <button
                      type='button'
                      onClick={() => removeTag(index)}
                      className='text-[#888] hover:text-red-500'
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <input
                type='text'
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='e.g. action, puzzle, casual'
                className='w-full bg-[#F5F5F5] text-base placeholder-[#c8c8c8] focus:outline-none'
              />
            </div>

            {/* ───────── Category ───────── */}
            <div className='border-b border-[#B9B9B9] bg-[#F5F5F5] px-4 py-2 rounded-[4px]'>
              <p className='mb-2 text-sm font-semibold text-[#69666A]'>
                Category
              </p>

              <div className='flex flex-wrap gap-2 mb-2'>
                {selectedCategories.map((category, index) => (
                  <div
                    key={index}
                    className='flex items-center gap-2 px-3 py-1 bg-white border border-[#B9B9B9] rounded-full text-sm text-[#333]'
                  >
                    <span>{category}</span>
                    <button
                      type='button'
                      onClick={() => removeCategory(index)}
                      className='text-[#888] hover:text-red-500'
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <select
                onChange={handleSelect}
                defaultValue=''
                className='w-full bg-[#F5F5F5] text-base text-[#333] focus:outline-none'
              >
                <option value='' disabled>
                  Choose category
                </option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* ───────── Thumbnail Upload ───────── */}
            <div className='space-y-2 border-b border-[#B9B9B9] bg-[#F5F5F5] p-4 rounded-tl-[4px] rounded-tr-[4px]'>
              <p className='text-base font-semibold text-[#69666A]'>
                Thumbnail
              </p>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <label className='cursor-pointer rounded-full border border-black px-4 py-2 text-sm font-medium bg-transparent'>
                    Change Image
                    <input
                      type='file'
                      accept='.png,.jpg'
                      className='hidden'
                      onChange={onThumbnailChange}
                    />
                  </label>
                  <span className='text-xs text-gray-500'>
                    png and jpg only
                  </span>
                </div>
                {(thumbnailFile || oldThumbnailUrl) && (
                  <div>
                    <span className='text-sm text-[#333]'>Selected:</span>

                    {thumbnailFile ? (
                      <img
                        className='h-40'
                        src={URL.createObjectURL(thumbnailFile)}
                        alt='Thumbnail preview'
                      />
                    ) : (
                      <RemoteFile keyPath={oldThumbnailUrl} className='h-40' />
                    )}
                  </div>
                )}
              </div>
            </div>

                        {/* ───────── Terms & Conditions ───────── */}
            <div className='p-2'>
              <label className='flex cursor-pointer select-none items-start gap-2 text-sm font-medium text-[#69666A]'>
                <input
                  type='checkbox'
                  checked={agree}
                  onChange={() => setAgree(!agree)}
                  className='mt-1 h-4 w-4 accent-[#FF0050]'
                />
                <span>
                  Agree with{' '}
                  <Link to='#' className='text-[#14D8CE] underline'>
                    term &amp; Conditions
                  </Link>
                </span>
              </label>
            </div>
          </div>

          {/* ───────── Action Buttons ───────── */}
          <div className='flex justify-end pt-6'>
            <button
              type='button'
              className='mr-3 rounded-full border border-black px-6 py-2 text-base'
              onClick={() => navigate('/publisher/gamics')}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              type='submit'
              className='rounded-full bg-[#FF0050] px-6 py-2 text-base text-white hover:opacity-90 disabled:opacity-50'
              disabled={
                !agree ||
                !gamicName ||
                // WL-DL-V1 follow-up: legacy `downloadLink` is no longer
                // user-editable (replaced by separate iOS / Android URL
                // inputs), so gating Submit on it left the button
                // permanently disabled whenever the stored
                // `download_url` was null/empty. `description` also
                // dropped here to match AddGamics, which already allows
                // wishlist-only gamics with optional description.
                // Backend submit payload still falls back to whichever
                // URL is present (line 205) so existing mobile clients
                // continue to receive a downloadUrl.
                selectedCategories.length === 0
              }
            >
              Submit Publish Request
            </button>
          </div>
        </div>

        {/* ───────── Right column – Preview ───────── */}
        <div className='flex flex-col justify-between bg-white rounded-[24px] p-6 lg:p-6 xl:p-8 2xl:p-10 w-full lg:w-[35vw] xl:w-[38vw] 2xl:w-[742px] min-w-0'>
          <h2 className='text-xl font-semibold xl:text-2xl'>
            Gamic Feed Preview
          </h2>

          {gameUrl ? (
            <div className='flex justify-center'>
              <iframe
                ref={iframeRef}
                src={instrumentedGameUrl || gameUrl}
                title='Game Preview'
                className='rounded-lg w-[244px] h-[436px] border'
                sandbox='allow-scripts allow-same-origin'
                onLoad={onPreviewLoad}
              />
            </div>
          ) : (
            <div className='flex justify-center'>
              <RemoteFile 
              keyPath={oldGameUrl}
              className='rounded-lg w-[424px] h-[636px] border'
              />
            </div>
          )}

          <div className='flex items-center justify-between'>
            <h3 className='text-lg font-semibold xl:text-xl'>Console</h3>
            <button className='rounded-full bg-[#FF0050] px-4 py-2 text-base text-white hover:opacity-90'>
              Test Gamic
            </button>
          </div>

          <div className='rounded-lg bg-[#F5F5F5] p-4'>
            <pre
              ref={logsRef}
              className='h-[25vh] overflow-x-scroll overflow-y-scroll font-mono text-xs scrollbar scrollbar-thumb-[#EEEEEE] scrollbar-track-transparent'
            >
              {logs.join('\n') || '[INFO] Console logs will appear here.'}
            </pre>
          </div>
        </div>
      </div>

      {/* ───────── Success Dialog ───────── */}
      <SuccessDialog
        open={open}
        onClose={handleClose}
        icon={<Icons.Yohoo />}
        heading='Congratulations!!!!'
        subheading='Your gamic edit is submitted successfully for approval, Will publish after checking.'
      />
    </>
  );
};

export default EditGamics;
